import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * useLockScreenFocus - Manages the user's single "Lock Screen focus" task.
 *
 * Design rules (v1):
 *  - Sign-in required. Anonymous users get a friendly gate message.
 *  - One focus task at a time. Selecting a new task REPLACES the old one.
 *  - Recurring push at chosen interval (15 / 30 / 60 min or custom).
 *  - Cancels automatically when the focused task is marked Done.
 *  - Backend re-reads the task from DB each fire so future reminders reflect
 *    whichever task the user currently has selected.
 */
export const useLockScreenFocus = () => {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState({
    enabled: false,
    taskId: null,
    taskName: null,
    intervalMinutes: null,
    loading: true,
  });

  // Fetch current lock-screen state
  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus({ enabled: false, taskId: null, taskName: null, intervalMinutes: null, loading: false });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/lockscreen/status`, { credentials: "include" });
      const data = await res.json();
      setStatus({
        enabled: !!data.enabled,
        taskId: data.taskId || null,
        taskName: data.taskName || null,
        intervalMinutes: data.intervalMinutes || null,
        loading: false,
      });
    } catch (err) {
      console.debug("[LockScreen] status fetch failed:", err?.message);
      setStatus((s) => ({ ...s, loading: false }));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Enable / replace focus task
  const enable = useCallback(async ({ taskId, taskName, intervalMinutes, pushSubscription, boardId }) => {
    if (!isAuthenticated) {
      return { ok: false, error: "signin_required" };
    }
    try {
      const res = await fetch(`${API_URL}/api/lockscreen/enable`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId, taskName, intervalMinutes, pushSubscription, boardId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { ok: false, error: err.detail || `HTTP ${res.status}` };
      }
      const data = await res.json();
      setStatus({
        enabled: true,
        taskId: data.taskId,
        taskName: data.taskName,
        intervalMinutes: data.intervalMinutes,
        loading: false,
      });
      return { ok: true, data };
    } catch (err) {
      return { ok: false, error: err?.message || "network_error" };
    }
  }, [isAuthenticated]);

  // Turn off globally
  const disable = useCallback(async () => {
    if (!isAuthenticated) return { ok: false };
    try {
      await fetch(`${API_URL}/api/lockscreen/disable`, { method: "POST", credentials: "include" });
      setStatus({ enabled: false, taskId: null, taskName: null, intervalMinutes: null, loading: false });
      return { ok: true };
    } catch (err) {
      console.debug("[LockScreen] disable failed:", err?.message);
      return { ok: false };
    }
  }, [isAuthenticated]);

  // Change interval on existing focus
  const setInterval = useCallback(async (intervalMinutes) => {
    if (!isAuthenticated) return { ok: false };
    try {
      const res = await fetch(`${API_URL}/api/lockscreen/interval`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intervalMinutes }),
      });
      if (!res.ok) return { ok: false };
      setStatus((s) => ({ ...s, intervalMinutes }));
      return { ok: true };
    } catch (err) {
      return { ok: false };
    }
  }, [isAuthenticated]);

  // Called when a task is marked Done - silently cancels if it's the focused task
  const notifyTaskCompleted = useCallback(async (taskId, taskName) => {
    if (!isAuthenticated) return;
    if (!status.enabled || status.taskId !== taskId) return;
    try {
      await fetch(`${API_URL}/api/lockscreen/task-completed`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId, taskName: taskName || "" }),
      });
      setStatus({ enabled: false, taskId: null, taskName: null, intervalMinutes: null, loading: false });
    } catch (err) {
      console.debug("[LockScreen] task-completed notify failed:", err?.message);
    }
  }, [isAuthenticated, status.enabled, status.taskId]);

  return {
    status,
    isAuthenticated,
    refresh,
    enable,
    disable,
    setInterval,
    notifyTaskCompleted,
  };
};

export default useLockScreenFocus;
